import {
  isStockTrackedProductType,
  type Product,
  PRODUCT_TYPES,
  type ProductCategory,
  productTypeSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  cn,
  DataTable,
  type DataTableColumn,
  Dialog,
  Drawer,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Select,
  StatusBadge,
  useToast,
} from "@station/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Boxes, FolderTree, Package, Pencil, Plus } from "lucide-react";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { targetMarginBody } from "../../../features/market/market-values.js";
import { apiDecimalToTrText } from "../../../features/pos/pos-values.js";
import { BarcodeEditor, flattenCategories, ProductForm } from "../../../features/product-form.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../lib/excel.js";
import { useDocumentTitle } from "../../../lib/hooks.js";
import { queryKeys } from "../../../lib/query.js";
import { useFeatures, usePermissions } from "../../../lib/session.js";

const searchSchema = z.object({
  q: z.string().max(100).optional().catch(undefined),
  type: productTypeSchema.optional().catch(undefined),
  category: z.string().uuid().optional().catch(undefined),
  active: z.enum(["true", "false"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/katalog/urunler")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ProductsPage,
});

function ProductsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate({ from: Route.fullPath });
  const filters = Route.useSearch();
  const permissions = usePermissions();
  const canManage = permissions.has("catalog.product.manage");
  // Market (Prompt 16): stok izlenen ürünün market ayrıntısı (stok, parti, tedarikçi, istasyon ayarı) stok kartındadır.
  const canOpenStockCard = useFeatures().has("MARKET") && permissions.has("market.read");
  useDocumentTitle(t("menu.products"));

  const [panel, setPanel] = useState<{ product: Product | null } | null>(null);
  const [searchText, setSearchText] = useState(filters.q ?? "");
  const pages = useCursorPages(JSON.stringify(filters));
  const setFilters = (patch: Partial<typeof filters>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });

  useEffect(() => {
    const handle = setTimeout(() => {
      if ((filters.q ?? "") !== searchText.trim()) setFilters({ q: searchText.trim() || undefined });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca yazılan metin değişince çalışmalı
  }, [searchText]);

  const query = {
    limit: 25,
    ...(filters.q ? { search: filters.q } : {}),
    ...(filters.type ? { productType: filters.type } : {}),
    ...(filters.category ? { categoryId: filters.category } : {}),
    ...(filters.active ? { isActive: filters.active } : {}),
  };
  const products = useQuery({
    queryKey: queryKeys.products({ ...query, cursor: pages.cursor ?? null }),
    queryFn: async () =>
      (
        await api.GET("/products", {
          params: { query: { ...query, ...(pages.cursor ? { cursor: pages.cursor } : {}) } },
        })
      ).data!,
    placeholderData: keepPreviousData,
  });
  const categories = useQuery({
    queryKey: queryKeys.productCategories,
    queryFn: async () => (await api.GET("/product-categories")).data!.items,
  });

  const columns = useMemo<DataTableColumn<Product>[]>(
    () => [
      {
        id: "name",
        header: t("catalog.fields.name"),
        hideable: false,
        cell: (row) => (
          <span className="flex items-center gap-2 font-medium">
            {row.color ? (
              <span
                className="size-3 shrink-0 rounded-full border"
                style={{ backgroundColor: row.color }}
                aria-hidden="true"
              />
            ) : null}
            {row.name}
          </span>
        ),
        exportValue: (row) => row.name,
      },
      {
        id: "code",
        header: t("catalog.fields.code"),
        cell: (row) => <span className="font-mono">{row.code}</span>,
        exportValue: (row) => row.code,
      },
      {
        id: "type",
        header: t("catalog.fields.productType"),
        cell: (row) => t(`enums.productType.${row.productType}`),
        exportValue: (row) => t(`enums.productType.${row.productType}`),
      },
      {
        id: "category",
        header: t("catalog.fields.category"),
        cell: (row) => row.categoryName ?? "—",
        exportValue: (row) => row.categoryName,
      },
      {
        id: "unit",
        header: t("catalog.fields.unit"),
        cell: (row) => t(`enums.productUnit.${row.unit}`),
        exportValue: (row) => t(`enums.productUnit.${row.unit}`),
      },
      {
        id: "vat",
        header: t("catalog.fields.vatRate"),
        align: "right",
        cell: (row) => <span className="tabular">{row.vatRate === null ? "—" : `%${row.vatRate}`}</span>,
        exportValue: (row) => (row.vatRate === null ? null : Number(row.vatRate)),
      },
      {
        id: "barcode",
        header: t("catalog.barcodes.title"),
        cell: (row) => (
          <span className="font-mono text-xs">
            {row.barcodes.find((item) => item.isPrimary)?.barcode ?? row.barcodes[0]?.barcode ?? "—"}
          </span>
        ),
        exportValue: (row) => row.barcodes.map((item) => item.barcode).join(", "),
      },
      {
        id: "accountingGroup",
        header: t("catalog.fields.accountingGroup"),
        defaultVisible: false,
        cell: (row) => <span className="font-mono text-xs">{row.accountingGroup}</span>,
        exportValue: (row) => row.accountingGroup,
      },
      {
        id: "businessUnit",
        header: t("catalog.fields.businessUnit"),
        defaultVisible: false,
        cell: (row) => t(`enums.businessUnit.${row.businessUnit}`),
        exportValue: (row) => t(`enums.businessUnit.${row.businessUnit}`),
      },
      {
        id: "posQuickKeySlot",
        header: t("catalog.fields.posQuickKeySlot"),
        align: "right",
        defaultVisible: false,
        cell: (row) => (
          <span className="tabular">{row.posQuickKeySlot === null ? "—" : row.posQuickKeySlot}</span>
        ),
        exportValue: (row) => row.posQuickKeySlot,
      },
      {
        id: "isAgeRestricted",
        header: t("catalog.fields.isAgeRestrictedShort"),
        // Anlam yalnızca renkle taşınmaz: yaş sınırlı ürün METİNLİ rozet alır, diğerleri tire.
        cell: (row) =>
          row.isAgeRestricted ? (
            <StatusBadge tone="warning">{t("catalog.fields.isAgeRestrictedShort")}</StatusBadge>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
        exportValue: (row) => (row.isAgeRestricted ? t("common.yes") : t("common.no")),
      },
      {
        id: "status",
        header: t("common.status"),
        cell: (row) => (
          <StatusBadge tone={row.isActive ? "success" : "neutral"}>
            {row.isActive ? t("common.active") : t("common.inactive")}
          </StatusBadge>
        ),
        exportValue: (row) => (row.isActive ? t("common.active") : t("common.inactive")),
      },
    ],
    [t],
  );
  const hasFilters = Boolean(filters.q ?? filters.type ?? filters.category ?? filters.active);

  return (
    <>
      <PageHeader
        title={t("menu.products")}
        description={t("catalog.description")}
        breadcrumb={[{ label: t("menu.groups.catalog") }, { label: t("menu.products") }]}
        actions={
          canManage ? (
            <Button onClick={() => setPanel({ product: null })}>
              <Plus aria-hidden="true" />
              {t("catalog.add")}
            </Button>
          ) : null
        }
      />
      <div className="grid gap-6 xl:grid-cols-[16rem_1fr]">
        <CategoryTree
          categories={categories.data ?? []}
          selected={filters.category ?? null}
          onSelect={(id) => setFilters({ category: id ?? undefined })}
          canManage={canManage}
        />
        <DataTable
          caption={t("menu.products")}
          columns={columns}
          rows={products.data?.items ?? []}
          rowKey={(row) => row.id}
          loading={products.isPending}
          error={products.error ? <Alert tone="danger">{errorMessage(products.error)}</Alert> : undefined}
          emptyState={
            <EmptyState
              icon={<Package />}
              title={hasFilters ? t("catalog.empty.filteredTitle") : t("catalog.empty.title")}
              description={
                hasFilters ? t("catalog.empty.filteredDescription") : t("catalog.empty.description")
              }
            />
          }
          filters={
            <>
              <FormField label={t("common.search")} className="w-60">
                {(control) => (
                  <Input
                    {...control}
                    type="search"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder={t("catalog.searchPlaceholder")}
                  />
                )}
              </FormField>
              <FormField label={t("catalog.fields.productType")} className="w-44">
                {(control) => (
                  <Select
                    {...control}
                    value={filters.type ?? ""}
                    onChange={(event) =>
                      setFilters({ type: (event.target.value || undefined) as typeof filters.type })
                    }
                  >
                    <option value="">{t("common.all")}</option>
                    {PRODUCT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {t(`enums.productType.${type}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField label={t("common.status")} className="w-32">
                {(control) => (
                  <Select
                    {...control}
                    value={filters.active ?? ""}
                    onChange={(event) =>
                      setFilters({ active: (event.target.value || undefined) as typeof filters.active })
                    }
                  >
                    <option value="">{t("common.all")}</option>
                    <option value="true">{t("common.active")}</option>
                    <option value="false">{t("common.inactive")}</option>
                  </Select>
                )}
              </FormField>
            </>
          }
          pagination={{
            hasPrevious: pages.hasPrevious,
            hasNext: products.data?.page.hasMore ?? false,
            onPrevious: pages.previous,
            onNext: () => pages.next(products.data?.page.nextCursor),
            pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
          }}
          onExport={async (visible) => {
            const rows = await fetchAllPages(
              async (cursor) =>
                (
                  await api.GET("/products", {
                    params: { query: { ...query, limit: 200, ...(cursor ? { cursor } : {}) } },
                  })
                ).data!,
            );
            await exportRowsToExcel({
              fileName: "urunler",
              sheetName: t("menu.products"),
              columns: visible,
              rows,
            });
          }}
          onRowClick={(row) => setPanel({ product: row })}
        />
      </div>

      <Drawer
        open={panel !== null}
        onOpenChange={(open) => !open && setPanel(null)}
        title={panel?.product ? panel.product.name : t("catalog.add")}
        {...(panel?.product ? { description: panel.product.code } : {})}
      >
        {panel ? (
          <div className="grid gap-6">
            {canManage ? (
              <ProductForm
                key={panel.product ? `${panel.product.id}-${panel.product.version}` : "new"}
                {...(panel.product ? { product: panel.product } : {})}
                categories={categories.data ?? []}
                onCancel={() => setPanel(null)}
                onSaved={(saved) => {
                  // Yeni ürün kaydedilince panel açık kalır: barkodlar hemen eklenebilsin.
                  setPanel({ product: saved });
                  toast({ tone: "success", title: t("common.saved") });
                }}
              />
            ) : (
              <Alert tone="info">{t("catalog.readOnly")}</Alert>
            )}
            {panel.product && canOpenStockCard && isStockTrackedProductType(panel.product.productType) ? (
              <section className="grid gap-2 border-t pt-4">
                <h2 className="text-sm font-semibold">{t("catalog.marketSection")}</h2>
                <p className="text-sm text-muted-foreground">{t("catalog.stockCardHint")}</p>
                <Link
                  to="/market/stok/$productId"
                  params={{ productId: panel.product.id }}
                  className="inline-flex items-center gap-1.5 text-sm underline underline-offset-4"
                >
                  <Boxes className="size-4" aria-hidden="true" />
                  {t("catalog.openStockCard")}
                </Link>
              </section>
            ) : null}
            {panel.product && canManage ? (
              <>
                <BarcodeEditor
                  key={`barcodes-${panel.product.id}-${panel.product.barcodes.length}`}
                  product={panel.product}
                  onSaved={(saved) => setPanel({ product: saved })}
                />
                <ActiveToggle product={panel.product} onSaved={(saved) => setPanel({ product: saved })} />
              </>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </>
  );
}

function ActiveToggle({
  product,
  onSaved,
}: {
  product: Product;
  onSaved: (product: Product) => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const toggle = useMutation({
    mutationFn: async () =>
      (
        await api.PATCH("/products/{id}", {
          params: { path: { id: product.id } },
          body: { isActive: !product.isActive, version: product.version },
        })
      ).data!,
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.products() });
      onSaved(saved as Product);
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });
  return (
    <section className="grid gap-2 border-t pt-4">
      <h2 className="text-sm font-semibold">{t("common.status")}</h2>
      <p className="text-sm text-muted-foreground">
        {product.isActive ? t("catalog.deactivateHint") : t("catalog.activateHint")}
      </p>
      <Button
        variant="outline"
        className="justify-self-start"
        loading={toggle.isPending}
        onClick={() => toggle.mutate()}
      >
        {product.isActive ? t("common.deactivate") : t("common.activate")}
      </Button>
    </section>
  );
}

function CategoryTree({
  categories,
  selected,
  onSelect,
  canManage,
}: {
  categories: readonly ProductCategory[];
  selected: string | null;
  onSelect: (id: string | null) => void;
  canManage: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ category: ProductCategory | null; parentId: string | null } | null>(
    null,
  );
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState<string | null>(null);
  // Market (Prompt 16): hedef brüt marj (%) — satış fiyatı önerisi kullanır; boşsa şirket varsayılanı geçerlidir.
  const [margin, setMargin] = useState("");

  const open = (category: ProductCategory | null, parent: string | null) => {
    setName(category?.name ?? "");
    setParentId(category ? category.parentId : parent);
    setMargin(apiDecimalToTrText(category?.targetMarginPercent));
    setDialog({ category, parentId: parent });
  };
  const save = useMutation({
    mutationFn: async () => {
      const body = { name, parentId, targetMarginPercent: targetMarginBody(margin) };
      return dialog?.category
        ? api.PATCH("/product-categories/{id}", {
            params: { path: { id: dialog.category.id } },
            body,
          })
        : api.POST("/product-categories", { body });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.productCategories }),
        queryClient.invalidateQueries({ queryKey: queryKeys.products() }),
      ]);
      setDialog(null);
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });
  const flat = flattenCategories(categories);

  return (
    <Card className="h-fit p-3">
      <div className="mb-2 flex items-center justify-between gap-2 px-1">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <FolderTree className="size-4 text-muted-foreground" aria-hidden="true" />
          {t("catalog.categories.title")}
        </h2>
        {canManage ? (
          <Button variant="ghost" size="sm" onClick={() => open(null, selected)}>
            <Plus aria-hidden="true" />
            {t("common.add")}
          </Button>
        ) : null}
      </div>
      <ul className="grid gap-0.5" aria-label={t("catalog.categories.title")}>
        <li>
          <button
            type="button"
            onClick={() => onSelect(null)}
            aria-current={selected === null ? "true" : undefined}
            className={cn(
              "w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted",
              selected === null && "bg-muted font-medium",
            )}
          >
            {t("catalog.categories.all")}
          </button>
        </li>
        {flat.map((category) => (
          <li
            key={category.id}
            className="group flex items-center"
            style={{ paddingLeft: `${category.depth * 0.875}rem` }}
          >
            <button
              type="button"
              onClick={() => onSelect(category.id)}
              aria-current={selected === category.id ? "true" : undefined}
              className={cn(
                "flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted",
                selected === category.id && "bg-muted font-medium",
                !category.isActive && "text-muted-foreground line-through",
              )}
            >
              <span className="truncate">{category.name}</span>
              <span className="text-xs tabular text-muted-foreground">{category.productCount}</span>
            </button>
            {canManage ? (
              <Button
                variant="ghost"
                size="icon"
                className="size-8 opacity-0 focus-visible:opacity-100 group-hover:opacity-100"
                aria-label={t("catalog.categories.edit", { name: category.name })}
                onClick={() => open(category, category.parentId)}
              >
                <Pencil aria-hidden="true" />
              </Button>
            ) : null}
          </li>
        ))}
      </ul>

      <Dialog
        open={dialog !== null}
        onOpenChange={(next) => !next && setDialog(null)}
        title={dialog?.category ? t("catalog.categories.editTitle") : t("catalog.categories.addTitle")}
      >
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim()) save.mutate();
          }}
        >
          <FormField label={t("catalog.categories.name")} required>
            {(control) => (
              <Input
                {...control}
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoFocus
                maxLength={120}
              />
            )}
          </FormField>
          <FormField label={t("catalog.categories.parent")}>
            {(control) => (
              <Select
                {...control}
                value={parentId ?? ""}
                onChange={(event) => setParentId(event.target.value || null)}
              >
                <option value="">{t("catalog.categories.root")}</option>
                {flat
                  .filter((category) => category.id !== dialog?.category?.id)
                  .map((category) => (
                    <option key={category.id} value={category.id}>
                      {"  ".repeat(category.depth) + category.name}
                    </option>
                  ))}
              </Select>
            )}
          </FormField>
          <FormField
            label={t("catalog.categories.targetMargin")}
            description={t("catalog.categories.targetMarginHint")}
          >
            {(control) => (
              <Input
                {...control}
                inputMode="decimal"
                className="text-right tabular"
                value={margin}
                onChange={(event) => setMargin(event.target.value)}
              />
            )}
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialog(null)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!name.trim()}>
              {t("common.save")}
            </Button>
          </div>
        </form>
      </Dialog>
    </Card>
  );
}
